import styled from 'styled-components';

/**
 * Shared calm action button used across all planning screens.
 *
 * Soft tinted background + matching text/icon color. Subtle enough
 * to not feel like a circus, but clearly colored enough to hint at
 * the action type.
 *
 * Variants:
 *   accent="primary"  (blue)   — most common action
 *   accent="success"  (green)  — secondary / add
 *   accent="purple"            — operational / sync
 *   accent="danger"   (red)    — destructive
 *   accent="warning"  (orange) — caution
 *   (no accent)                — neutral, plain gray
 */
/**
 * Helper: convert a hex color to a tinted rgba string.
 * Used to create the soft background for ActionBtn.
 *
 *   hexToTint('#2185d0', 0.12) -> 'rgba(33, 133, 208, 0.12)'
 */
function hexToTint(hex: string, alpha: number): string {
    const h = hex.replace('#', '');
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export const ActionBtn = styled.button<{ accent?: string }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 5px 10px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.12s ease;
    font-family: inherit;
    background: ${props => props.accent ? hexToTint(props.accent, 0.12) : '#f5f6f7'};
    color: ${props => props.accent ? props.accent : '#1a1d23'};
    border: 1px solid ${props => props.accent ? hexToTint(props.accent, 0.25) : '#e0e3e8'};
    box-shadow: none;
    &:hover {
        background: ${props => props.accent ? hexToTint(props.accent, 0.22) : '#e8eaee'};
        border-color: ${props => props.accent ? hexToTint(props.accent, 0.4) : '#c5cad2'};
    }
    &:active {
        background: ${props => props.accent ? hexToTint(props.accent, 0.32) : '#e0e3e8'};
    }
    &:focus-visible {
        outline: 2px solid ${props => props.accent || '#2185d0'};
        outline-offset: 1px;
    }
    &:disabled {
        opacity: 0.5; cursor: not-allowed;
    }
`;

/**
 * The icon inside an ActionBtn. Uses the same accent color as the
 * surrounding button — so the icon and text are both the same calm
 * color, matching the soft background.
 */
export const ActionIcon = styled.span<{ color: string }>`
    color: ${props => props.color};
    font-size: 13px;
    line-height: 1;
    display: inline-flex;
`;

export const ACTION_ACCENTS = {
    primary: '#2185d0',
    success: '#21ba45',
    warning: '#f0a721',
    danger:  '#db2828',
    purple:  '#805ad5',
    purpleDeep: '#6b46c1',
};

